import { getIsLoggedIn } from '@/src/features/auth/utils/getIsLoggedIn'
import { routeToPath, type TEventOriginParam } from '@/src/general/constants/routes'
import { useMakeIsGranted } from '@/src/general/role-access'
import { isNative } from '@/src/general/utils/isNative'
import { useRoutes } from '@/src/general/utils/routes'
import { useTranslate } from '@/src/lang/useTranslate'

import type { TGetCommunityApiData } from '../../../../api/getCommunity.types'
import { COMMUNITY_STATUS, EVENT_ORIGIN } from '../../../../models/community.constants'

type TUseCommunityRedirectionsParams = {
  community?: TGetCommunityApiData
  forbidden?: boolean
  id?: number
  slug?: string
} & TEventOriginParam

export const useCommunityRedirections = ({
  community,
  forbidden,
  id,
  origin,
  slug,
}: TUseCommunityRedirectionsParams) => {
  const { isGranted } = useMakeIsGranted()
  const { routes } = useRoutes()
  const { t } = useTranslate()

  // If the user is not logged in, redirect to the communities page with a flash message
  if (forbidden && !getIsLoggedIn()) {
    if (!id || !slug) {
      return {
        redirectPath: routes.communities({
          action: 'message',
          message: t('error.403.title'),
        }),
      }
    }
    const communityAdminRoute = routeToPath(routes.communityAdmin({ id, slug }))
    return {
      redirectPath: isNative()
        ? routes.authConnect({ redirectTo: communityAdminRoute })
        : routes.connect(communityAdminRoute),
    }
  }

  if (forbidden && getIsLoggedIn()) {
    return {
      redirectPath: routes.communities({
        action: 'message',
        message: t('error.403.title'),
      }),
    }
  }

  if (!community || !id || !slug) return { redirectPath: undefined }

  const canEditContent = isGranted('canManageContent', community)

  if (
    community.status === COMMUNITY_STATUS.VALID &&
    !getIsLoggedIn() &&
    origin !== EVENT_ORIGIN.COMMUNITY_PUBLICATIONS_TAB
  ) {
    return { redirectPath: routes.communityLegacy({ id, slug }) }
  }

  if ((community.status === COMMUNITY_STATUS.CHECK || community.status === COMMUNITY_STATUS.DRAFT) && getIsLoggedIn()) {
    if (canEditContent) {
      const communityAdminRoute = routeToPath(routes.communityAdmin({ id, slug }))
      return { redirectPath: communityAdminRoute }
    } else {
      // This case should not happens, the backend prevent it by sending a 403 error
      return {
        redirectPath: routes.communities({
          action: 'message',
          message: t('error.403.title'),
        }),
      }
    }
  }

  return { redirectPath: undefined }
}
